export enum SupportedDataResidency {
  US = 'us',
  UAE = 'uae',
}

export interface DataResidencyConfig {
  dataResidency: SupportedDataResidency;
  cognitoAwsRegion: string;
  dynamoDbAwsRegion: string;
  lambdaAwsRegion: string;
  apiEndpointSuffix: string;
}

export const DATA_RESIDENCY_CONFIGS: Record<SupportedDataResidency, DataResidencyConfig> = {
  [SupportedDataResidency.US]: {
    dataResidency: SupportedDataResidency.US,
    cognitoAwsRegion: 'us-east-1',
    dynamoDbAwsRegion: 'us-west-2',
    lambdaAwsRegion: 'us-east-1',
    apiEndpointSuffix: '',
  },
  [SupportedDataResidency.UAE]: {
    dataResidency: SupportedDataResidency.UAE,
    cognitoAwsRegion: 'me-central-1',
    dynamoDbAwsRegion: 'me-central-1',
    lambdaAwsRegion: 'me-central-1',
    apiEndpointSuffix: '-uae',
  },
};

export class DataResidencyManager {
  private static instance: DataResidencyManager;
  private currentDataResidency: SupportedDataResidency = SupportedDataResidency.US;

  private constructor() {}

  public static getInstance(): DataResidencyManager {
    if (!DataResidencyManager.instance) {
      DataResidencyManager.instance = new DataResidencyManager();
    }
    return DataResidencyManager.instance;
  }

  public setDataResidency(dataResidency: SupportedDataResidency): void {
    this.currentDataResidency = dataResidency;
    localStorage.setItem('peekapak.userDataResidency', dataResidency);
  }

  public getCurrentDataResidency(): SupportedDataResidency {
    // Check localStorage first
    const storedDataResidency = localStorage.getItem('peekapak.userDataResidency') as SupportedDataResidency;
    if (storedDataResidency && Object.values(SupportedDataResidency).includes(storedDataResidency)) {
      this.currentDataResidency = storedDataResidency;
    }

    return this.currentDataResidency;
  }

  public getCurrentConfig(): DataResidencyConfig {
    return DATA_RESIDENCY_CONFIGS[this.getCurrentDataResidency()];
  }

  public isUAEDataResidency(): boolean {
    return this.getCurrentDataResidency() === SupportedDataResidency.UAE;
  }

  public getApiSubdomain(): string {
    const config = this.getCurrentConfig();
    return config.apiEndpointSuffix === '-uae' ? 'api-uae' : 'api';
  }

  public getCognitoConfiguration(dataResidency?: SupportedDataResidency) {
    // Use provided data residency or fall back to current
    const targetDataResidency = dataResidency || this.getCurrentDataResidency();

    if (targetDataResidency === SupportedDataResidency.UAE) {
      if (import.meta.env.MODE === 'development') {
        return import('@peekapak/common/configuration/devCognitoConfigurationUAE.json');
      } else {
        return import('@peekapak/common/configuration/prodCognitoConfigurationUAE.json');
      }
    } else {
      if (import.meta.env.MODE === 'development') {
        return import('@peekapak/common/configuration/devCognitoConfiguration.json');
      } else {
        return import('@peekapak/common/configuration/prodCognitoConfiguration.json');
      }
    }
  }

  public async detectUserDataResidencyFromIP(): Promise<SupportedDataResidency> {
    try {
      // Use a geolocation API to determine user's required data residency
      const response = await fetch('/api/commonservices/users/get-country-origin');
      const data = await response.json();

      // UAE country codes that require UAE data residency (stored in me-central-1 AWS region)
      const uaeCountryCodes = ['AE', 'UAE'];

      if (data.countryCode && uaeCountryCodes.includes(data.countryCode.toUpperCase())) {
        return SupportedDataResidency.UAE;
      }

      return SupportedDataResidency.US;
    } catch (error) {
      console.warn('Failed to detect user data residency, defaulting to US:', error);
      return SupportedDataResidency.US;
    }
  }

  public async initializeDataResidency(): Promise<void> {
    // Check if data residency is already set in localStorage
    const storedDataResidency = localStorage.getItem('peekapak.userDataResidency');
    if (
      storedDataResidency &&
      Object.values(SupportedDataResidency).includes(storedDataResidency as SupportedDataResidency)
    ) {
      this.setDataResidency(storedDataResidency as SupportedDataResidency);
      return;
    }

    // Detect data residency from IP if not set
    const detectedDataResidency = await this.detectUserDataResidencyFromIP();
    this.setDataResidency(detectedDataResidency);
  }
}

export const dataResidencyManager = DataResidencyManager.getInstance();
